import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { GeneralLedgerService } from '../../services/general-ledger.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { PrintService } from '../../services/print.service';

@Component({
	selector: 'app-shield-compliance',
	templateUrl: './shield-compliance.component.html',
	styleUrls: ['./shield-compliance.component.scss']
})
export class ShieldComplianceComponent implements OnInit {

	@ViewChild('print_table') printTableRef: ElementRef;

	color = '';
	loading = false;
	exporting = false;
	formGroup: UntypedFormGroup;
	periods: any[] = [];
	from_period_options: any[] = [];
	to_period_options: any[] = [];

	report: any = null;
	summary: any = null;
	attestation: any = null;
	byBrand: any[] = [];
	invoices: any[] = [];

	expandedBrand: string | null = null;
	expandedLine: string | null = null;

	brandColumns = ['suppname', 'invoice_count', 'gross_amount', 'brand_amount', 'metrc_amount', 'moov_revenue', 'processing_fees', 'customer_paid', 'brand_paid_out'];

	constructor(
		private location: Location,
		private fb: UntypedFormBuilder,
		private generalLedgerService: GeneralLedgerService,
		private globalSearchService: GlobalSearchService,
		private printService: PrintService,
	) {}

	ngOnInit(): void {
		this.color = this.globalSearchService.getColor();
		this.generalLedgerService.getPeriods().subscribe((results: any) => {
			this.periods = results.periods;
			this.from_period_options = results.periods;
			this.to_period_options = results.periods;

			this.formGroup = this.fb.group({
				range_mode: ['period'],
				from_period: [results.default_fromperiod, Validators.required],
				to_period:   [results.default_toperiod,   Validators.required],
				from_date: [this.defaultPeriodStart(results.default_fromperiod)],
				to_date: [this.periodDateValue(results.default_toperiod)],
			});
		});
	}

	updateToOptions(value: any) {
		this.to_period_options = this.periods.filter(p => parseInt(p.periodno) >= parseInt(value));
		if (this.formGroup?.get('range_mode')?.value === 'period') {
			this.formGroup.patchValue({ from_date: this.defaultPeriodStart(value) }, { emitEvent: false });
		}
	}

	updateFromOptions(value: any) {
		this.from_period_options = this.periods.filter(p => parseInt(p.periodno) <= parseInt(value));
		if (this.formGroup?.get('range_mode')?.value === 'period') {
			this.formGroup.patchValue({ to_date: this.periodDateValue(value) }, { emitEvent: false });
		}
	}

	run() {
		if (!this.formGroup.valid) {
			return;
		}
		this.loading = true;
		this.report = null;
		this.generalLedgerService.getShieldReport(this.reportPayload()).subscribe((res: any) => {
			this.loading = false;
			this.report      = res;
			this.summary     = res.summary;
			this.attestation = res.attestation || null;
			this.byBrand     = res.by_brand  || [];
			this.invoices    = res.invoices  || [];
		});
	}

	private exportTitle = 'Accounting Data Attestation';

	private periodLabel(periodno: any): string {
		const p = this.periods.find(x => String(x.periodno) === String(periodno));
		return p ? p.lastdate_in_period : String(periodno ?? '');
	}

	private localDateValue(isoDate: string): Date | string {
		if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
			return isoDate;
		}
		const y = Number(isoDate.slice(0, 4));
		const m = Number(isoDate.slice(5, 7)) - 1;
		const d = Number(isoDate.slice(8, 10));
		return new Date(y, m, d);
	}

	private periodDateValue(periodno: any): Date | string {
		return this.localDateValue(this.periodLabel(periodno));
	}

	private defaultPeriodStart(periodno: any): Date | string {
		const label = this.periodLabel(periodno);
		if (!/^\d{4}-\d{2}-\d{2}$/.test(label)) {
			return label;
		}
		return this.localDateValue(label.slice(0, 8) + '01');
	}

	private formatDateForApi(value: any): string {
		if (!value) {
			return '';
		}
		if (typeof value === 'string') {
			return value.slice(0, 10);
		}
		const d = value instanceof Date ? value : new Date(value);
		if (isNaN(d.getTime())) {
			return '';
		}
		const y = d.getFullYear();
		const m = String(d.getMonth() + 1).padStart(2, '0');
		const day = String(d.getDate()).padStart(2, '0');
		return `${y}-${m}-${day}`;
	}

	private reportPayload(): any {
		const mode = this.formGroup?.get('range_mode')?.value || 'period';
		if (mode === 'date') {
			return {
				range_mode: 'date',
				from_date: this.formatDateForApi(this.formGroup.get('from_date')?.value),
				to_date: this.formatDateForApi(this.formGroup.get('to_date')?.value),
				from_period: this.formGroup.get('from_period')?.value,
				to_period: this.formGroup.get('to_period')?.value,
			};
		}
		return {
			range_mode: 'period',
			from_period: this.formGroup.get('from_period')?.value,
			to_period: this.formGroup.get('to_period')?.value,
		};
	}

	setRangeMode(mode: 'period' | 'date'): void {
		this.formGroup.patchValue({ range_mode: mode });
	}

	setDatePreset(preset: 'this_week' | 'last_week' | 'this_month' | 'last_month'): void {
		const today = new Date();
		const start = new Date(today);
		const end = new Date(today);
		if (preset === 'this_week' || preset === 'last_week') {
			const day = today.getDay();
			const mondayOffset = day === 0 ? -6 : 1 - day;
			start.setDate(today.getDate() + mondayOffset);
			end.setDate(start.getDate() + 6);
			if (preset === 'last_week') {
				start.setDate(start.getDate() - 7);
				end.setDate(end.getDate() - 7);
			}
		} else {
			start.setDate(1);
			if (preset === 'last_month') {
				start.setMonth(start.getMonth() - 1);
			}
			end.setFullYear(start.getFullYear(), start.getMonth() + 1, 0);
		}
		this.formGroup.patchValue({
			range_mode: 'date',
			from_date: start,
			to_date: end,
		});
	}

	private exportSubtitle(): string {
		if (this.formGroup?.get('range_mode')?.value === 'date') {
			return `${this.formatDateForApi(this.formGroup.get('from_date')?.value)} to ${this.formatDateForApi(this.formGroup.get('to_date')?.value)}`;
		}
		const fp = this.formGroup?.get('from_period')?.value;
		const tp = this.formGroup?.get('to_period')?.value;
		return `Periods ${this.periodLabel(fp)} to ${this.periodLabel(tp)}`;
	}

	private exportDownloadBasename(): string {
		const day = new Date().toISOString().slice(0, 10);
		if (this.formGroup?.get('range_mode')?.value === 'date') {
			return `AccountingDataAttestation_${this.formatDateForApi(this.formGroup.get('from_date')?.value)}_${this.formatDateForApi(this.formGroup.get('to_date')?.value)}_${day}`;
		}
		const fp = this.formGroup?.get('from_period')?.value;
		const tp = this.formGroup?.get('to_period')?.value;
		const p = (fp != null && fp !== '') ? `_p${String(fp)}-p${String(tp)}` : '';
		return `AccountingDataAttestation${p}_${day}`;
	}

	exportXlsx(): void {
		if (!this.report || !this.printTableRef?.nativeElement) {
			return;
		}
		this.exporting = true;
		const encoded = this.globalSearchService.base64encode(this.printTableRef.nativeElement.innerHTML);
		const base = this.exportDownloadBasename();
		const data = { content: encoded, filename: base, title: this.exportTitle, subtitle: this.exportSubtitle() };
		this.printService.xls(data).subscribe({
			next: (result: any) => {
				this.exporting = false;
				this.globalSearchService.downloadXls(result.content, `${base}.xlsx`);
			},
			error: () => { this.exporting = false; },
		});
	}

	exportPdf(): void {
		if (!this.report || !this.printTableRef?.nativeElement) {
			return;
		}
		this.exporting = true;
		const encoded = this.globalSearchService.base64encode(this.printTableRef.nativeElement.innerHTML);
		const base = this.exportDownloadBasename();
		const data = { content: encoded, filename: base, landscape: true, title: this.exportTitle, subtitle: this.exportSubtitle() };
		this.printService.pdf(data).subscribe({
			next: (result: any) => {
				this.exporting = false;
				this.globalSearchService.downloadPdf(result.content, `${base}.pdf`);
			},
			error: () => { this.exporting = false; },
		});
	}

	copyToClipboard(val: number) {
		navigator.clipboard.writeText(val.toFixed(2));
	}

	toggleLine(key: string) {
		this.expandedLine = this.expandedLine === key ? null : key;
	}

	get balanceOk(): boolean {
		return !this.attestation || Math.abs(this.attestation.balance_check || 0) < 0.01;
	}

	// Distribution gross profit is net sales less brand payouts, so it must equal what
	// MOOV actually kept: the distribution fee plus processing fees. A variance means
	// an invoice's split does not add back to its total.
	get marginVariance(): number {
		if (!this.summary) {
			return 0;
		}
		const kept = (this.summary.moov_revenue || 0) + (this.summary.processing_fees || 0);
		return Math.round(((this.summary.shield_gross_profit || 0) - kept) * 100) / 100;
	}

	get marginTies(): boolean {
		return Math.abs(this.marginVariance) < 0.01;
	}

	toggleBrand(supplierno: string) {
		this.expandedBrand = this.expandedBrand === supplierno ? null : supplierno;
	}

	brandInvoices(supplierno: string) {
		return this.invoices.filter(i => i.supplierno === supplierno);
	}

	invoiceStatus(inv: any): string {
		if (inv.customer_paid >= inv.gross_amount) {
			return 'Paid';
		}
		if (inv.customer_paid > 0) {
			return 'Partial';
		}
		return 'Unpaid';
	}

	back() { this.location.back(); }
}
